/**
 * Invoice domain schemas, derived from the generated Zod.
 *
 * The app-side enrichments layered on top: the decimal-string → number
 * coercions the UI does math on (`decimalNumber`/`decimalNumberNullable`), the
 * date/year refinements, and the cross-field form `superRefine`s.
 *
 * The nested objects overridden to nullable (`serviceProvider`, `consultant`,
 * `parentInvoice`, `emailContext`, `uploadedBy`) all arrive null for an
 * unassigned / non-adjusted / manually-created invoice.
 *
 * See ./README.md.
 */
import { z } from 'zod';

import { InvoiceKindEnum, InvoiceTypeEnum } from '~/data/generated/model';
import {
  InvoicesListResponse,
  InvoicesPartialUpdateResponse,
  InvoicesRetrieveResponse,
} from '~/data/generated/zod/invoices/invoices.zod';
import { ServiceProvidersRetrieveResponse } from '~/data/generated/zod/service-providers/service-providers.zod';
import { baseLinkedAttachmentSchema } from '~/data/schemas/attachment';
import { carDisplaySchema } from '~/data/schemas/car';
// webapp→webapp import; not the forbidden types → webapp cycle.
import { documentUploadSourceValues, emailContextSchema } from '~/data/schemas/documentReview';
import { type Portfolio } from '~/data/schemas/portfolio';
import { numberFilterParamsSchema } from '~/data/schemas/tableFilters';
import { api_userSchema, userDisplaySchema } from '~/data/schemas/user';

import { decimalNumberNullable } from './generatedZodHelpers';
import {
  type OneOrMore,
  VALID_DATE_YEAR_MESSAGE,
  validateDateYear,
  validDateTime,
  type Values,
} from './zodHelpers';

// Wire sends the line-item `amount` as a decimal STRING; the UI sums it.
export const lineItemSchema = z.object({
  id: z.number().optional(),
  description: z.string(),
  amount: z.coerce.number(),
});
export type LineItem = z.infer<typeof lineItemSchema>;

const invoiceAttachmentSchema = baseLinkedAttachmentSchema.extend({
  isVoid: z.boolean(),
});
export type InvoiceAttachment = z.infer<typeof invoiceAttachmentSchema>;

export const InvoiceType = {
  Maintenance: InvoiceTypeEnum.maintenance,
  Modernization: InvoiceTypeEnum.modernization,
  Callback: InvoiceTypeEnum.callback,
  Inspection: InvoiceTypeEnum.inspection,
  Repair: InvoiceTypeEnum.repair,
  Fee: InvoiceTypeEnum.fee,
} as const;

export const InvoiceStatus = {
  Pending: 'pending',
  Approved: 'approved',
  Rejected: 'rejected',
  PartialApproval: 'partial-approval',
  PendingData: 'pending-data',
  Reset: 'reset',
} as const;

export const InvoiceKind = {
  Invoice: InvoiceKindEnum.invoice,
  Proposal: InvoiceKindEnum.proposal,
  Credit: InvoiceKindEnum.credit,
} as const;

const InvoiceRejectionCategory = {
  BillingRateIncorrect: 'billing-rate-incorrect',
  NoTimeTickets: 'no-time-tickets',
  RequiredHoursIncorrect: 'required-hours-incorrect',
  MiscellaneousFees: 'miscellaneous-fees',
  CoveredByContract: 'covered-by-contract',
  WorkNotRequired: 'work-not-required',
  MaterialMarkupIncorrect: 'material-markup-incorrect',
  MaterialQuantityIncorrect: 'material-quantity-incorrect',
  PreNegotiatedPriceNotFollowed: 'pre-negotiated-price-not-followed',
  Other: 'other',
} as const;

export const InvoiceRejectionCategoryOptions = [
  [InvoiceRejectionCategory.BillingRateIncorrect, 'Billing Rate Incorrect'],
  [InvoiceRejectionCategory.NoTimeTickets, 'No Time Tickets'],
  [InvoiceRejectionCategory.RequiredHoursIncorrect, 'Required Hours Incorrect'],
  [InvoiceRejectionCategory.MiscellaneousFees, 'Miscellaneous Fees'],
  [InvoiceRejectionCategory.CoveredByContract, 'Covered By Contract'],
  [InvoiceRejectionCategory.WorkNotRequired, 'Work Not Required'],
  [InvoiceRejectionCategory.MaterialMarkupIncorrect, 'Material Markup Incorrect'],
  [InvoiceRejectionCategory.MaterialQuantityIncorrect, 'Material Quantity Incorrect'],
  [InvoiceRejectionCategory.PreNegotiatedPriceNotFollowed, 'Pre-negotiated Price Not Followed'],
  [InvoiceRejectionCategory.Other, 'Other'],
] as const;

type InvoiceRejectionCategoryKey = Values<typeof InvoiceRejectionCategory>;
const invoiceRejectionCategoryValues = Object.values(
  InvoiceRejectionCategory,
) as OneOrMore<InvoiceRejectionCategoryKey>;

export enum InvoiceActionType {
  APPROVE = 'APPROVE',
  REJECT = 'REJECT',
  PARTIAL = 'PARTIAL',
  REQUEST_DATA = 'REQUEST_DATA',
  RESET = 'RESET',
}

const invoiceKindLabels = {
  [InvoiceKind.Invoice]: 'Invoice',
  [InvoiceKind.Proposal]: 'Proposal',
  [InvoiceKind.Credit]: 'Credit',
} satisfies Record<InvoiceKindEnum, string>;
export const invoiceKindOptions = Object.entries(invoiceKindLabels) as [InvoiceKindEnum, string][];
export type InvoiceKindKey = InvoiceKindEnum;
export const invoiceKindValues = Object.values(InvoiceKindEnum) as OneOrMore<InvoiceKindKey>;

const invoiceTypeLabels = {
  [InvoiceType.Maintenance]: 'Maintenance',
  [InvoiceType.Modernization]: 'Modernization',
  [InvoiceType.Callback]: 'Callback',
  [InvoiceType.Inspection]: 'Inspection',
  [InvoiceType.Repair]: 'Repair',
  [InvoiceType.Fee]: 'Late Fee / Service Fee',
} satisfies Record<InvoiceTypeEnum, string>;
export const invoiceTypeOptions = Object.entries(invoiceTypeLabels) as [InvoiceTypeEnum, string][];

export type InvoiceTypeKey = InvoiceTypeEnum;
export const invoiceTypeValues = Object.values(InvoiceTypeEnum) as OneOrMore<InvoiceTypeKey>;

export const invoiceStatusOptions = [
  [InvoiceStatus.Pending, 'Pending'],
  [InvoiceStatus.Approved, 'Approved'],
  [InvoiceStatus.Rejected, 'Rejected'],
  [InvoiceStatus.PartialApproval, 'Partial Approval'],
  [InvoiceStatus.PendingData, 'Pending Data'],
  // This will never be used since the API will set the status to `Pending` when the invoice is reset
  // but it is needed to make the type system happy
  [InvoiceStatus.Reset, 'Reset'],
] as const;
export type InvoiceStatusKey = Values<typeof InvoiceStatus>;
export const invoiceStatusValues = Object.values(InvoiceStatus) as OneOrMore<InvoiceStatusKey>;

export const invoiceKindToLabelMap = new Map(invoiceKindOptions);
export const invoiceTypeToLabelMap = new Map(invoiceTypeOptions);
export const invoiceStatusToLabelMap = new Map(invoiceStatusOptions);
export const invoiceRejectionCategoryToLabelMap = new Map(InvoiceRejectionCategoryOptions);

export const invoiceStatusToColorMap = new Map([
  [InvoiceStatus.Approved, 'success'],
  [InvoiceStatus.Rejected, 'error'],
  [InvoiceStatus.Pending, 'info'],
  [InvoiceStatus.PendingData, 'info'],
  [InvoiceStatus.PartialApproval, 'warning'],
  // This will never be used since the API will set the status to `Pending` when the invoice is reset
  // but it is needed to make the type system happy
  [InvoiceStatus.Reset, 'warning'],
] as const);

// Enum narrowings the label maps key on, plus the decimal coercion on amounts
// the UI formats. Amounts left as strings are never summed.
export const RelatedInvoiceSchema = InvoicesRetrieveResponse.shape.relatedInvoices.element.extend({
  status: z.enum(invoiceStatusValues),
  kind: z.enum(invoiceKindValues),
  attachments: z.array(invoiceAttachmentSchema).default([]),
});

// `.unwrap()` the generated `parentInvoice`: it is now `.nullable()` at source
// (AMP-633), and `.extend()` exists only on the inner object. Nullability is
// re-applied where the schema is used (`ParentInvoiceSchema.nullable()` below).
const ParentInvoiceSchema = InvoicesRetrieveResponse.shape.parentInvoice.unwrap().extend({
  // The parent's proposedAmount is fed straight to formatCurrency; coerce the
  // DRF decimal string to a number (nullable/absent → null).
  proposedAmount: decimalNumberNullable.optional(),
});

const AdjustmentInvoiceSchema = InvoicesRetrieveResponse.shape.adjustments.element.extend({
  status: z.enum(invoiceStatusValues),
  proposedAmount: decimalNumberNullable.optional(),
  // The generated element types adjustmentSource nullish (a union with '' and
  // null); on a real adjustment it is always set, and the AdjustmentInvoice type
  // consumers read expects the narrow enum.
  adjustmentSource: z.enum(['manual', 'agent']),
  // savingsDelta is a SerializerMethodField returning Decimal|None, which DRF
  // renders as a number (already `zod.number().nullable()` on the wire), so it
  // is inherited from the generated element — not coerced.
});

export type AdjustmentInvoice = z.infer<typeof AdjustmentInvoiceSchema>;

const insightSentiment = z.enum(['supports-approval', 'supports-rejection', 'informational']);

const insightSchema = z.object({
  insight: z.string(),
  reasoning: z.string(),
  sentiment: insightSentiment.optional(),
  contractSection: z.string().nullable().optional(),
  contractQuote: z.string().nullable().optional(),
});

const aiReviewStatus = z.enum([
  'approved',
  'rejected',
  'partial-approval',
  'pending-data',
  'reset',
]);

const insightsSchema = z.object({
  insights: z.array(insightSchema).default([]),
  shouldBePaid: z.boolean(),
  primaryInsight: z.string(),
  runId: z.string().nullable(),
  timestamp: z.string(),
  status: aiReviewStatus.optional(),
  reviewedAmount: z.number().nullable().optional(),
  confidence: z.number().optional(),
  rejectionCategories: z.array(z.enum(invoiceRejectionCategoryValues)).optional(),
  missingEvidence: z.array(z.string()).optional(),
});

export type RelatedInvoice = z.infer<typeof RelatedInvoiceSchema>;

export const invoiceSchema = InvoicesRetrieveResponse.omit({
  // Present on the wire but kept off the base Invoice: `tickets` is re-added on
  // `InvoiceWithTickets` (the list-row type).
  tickets: true,
}).extend({
  number: z
    .string()
    .min(1, { message: 'This field is required' })
    .max(100, { message: 'This field must be less than 100 characters' }),
  // Invoices may carry future dates (e.g. prepaid maintenance billed in advance),
  // so we only validate that the date is real and within the supported year range.
  // Nullable: the API returns a null date for invoices whose date was never
  // extracted (model DateField(null=True)).
  date: validDateTime().refine(validateDateYear, { message: VALID_DATE_YEAR_MESSAGE }).nullable(),
  kind: z.enum(invoiceKindValues),
  type: z.enum(invoiceTypeValues),
  status: z.enum(invoiceStatusValues),
  // Non-null on the wire; optional only because fixtures omit the key.
  disputeNeedsAttention: z.boolean().optional(),
  // region is DateField-less; the wire types it nullable. Kept nullish so both a
  // null value and an absent key (lean rows / fixtures) parse.
  region: z.string().nullish(),
  property: InvoicesRetrieveResponse.shape.property
    .unwrap()
    .extend({ timeZone: z.string().nullish() }),
  // The trimmed `{id, name}` service-provider dict. Both keys are typed
  // required, as the hand-written schema had them: the invoice serializer's own
  // copy types them optional, which no consumer is written for.
  serviceProvider: ServiceProvidersRetrieveResponse.pick({ id: true, name: true }).nullable(),
  carsServiced: z.array(carDisplaySchema),
  consultant: userDisplaySchema.nullable(),
  lineItems: z.array(lineItemSchema),
  // Wire sends an unset decimal as `''`, and `Number('') === 0` would turn an
  // absent amount into $0.00 — `decimalNumberNullable` maps '' → null first.
  proposedAmount: decimalNumberNullable.optional(),
  reviewedAmount: decimalNumberNullable.optional(),
  // The API's own `Invoice.approved_amount`. Optional so a fixture or an older
  // cached payload without it still parses; see `invoiceApprovedAmountToShow`.
  approvedAmount: decimalNumberNullable.optional(),
  savings: decimalNumberNullable.optional(),
  // Rendered as a number, but coerce so a decimal-string value still parses.
  projectedSavings: z.coerce.number().nullable().optional(),
  rejectionCategory: z.array(z.enum(invoiceRejectionCategoryValues)).nullish(),
  // A media URL: the generated `.url()` check throws on a relative path.
  stampFile: z.string().nullable(),
  attachments: z.array(invoiceAttachmentSchema).default([]),
  autoUpload: z.boolean().default(false),
  relatedInvoices: z.array(RelatedInvoiceSchema).default([]),
  parentInvoice: ParentInvoiceSchema.nullable().default(null),
  adjustmentSource: z.enum(['manual', 'agent']).nullable().default(null),
  creditedInvoice: InvoicesRetrieveResponse.shape.creditedInvoice.default(null),
  credits: InvoicesRetrieveResponse.shape.credits.default([]),
  countsTowardSpend: InvoicesRetrieveResponse.shape.countsTowardSpend.default(null),
  adjustments: z.array(AdjustmentInvoiceSchema).default([]),
  // Typed view over the generated `unknown` insights blob; null when the AI
  // review has not run.
  insights: insightsSchema.nullable(),
  source: z.enum(documentUploadSourceValues).nullable().optional(),
  emailContext: emailContextSchema.nullable().optional(),
  uploadedBy: userDisplaySchema.nullable().optional(),
  uploadedAt: z.string().nullable().optional(),
  createdAt: z.string().nullable().optional(),
  // activeContractId inherits the generated base: `get_active_contract_id`
  // (services/api/main/views/api/serializers/invoice.py) is `allow_null=True`
  // at source as of AMP-633, so the generated `.uuid().nullable()` already
  // matches the real null it returns whenever no governing contract resolves.
});

export const isInvoiceAutoUpload = (invoice: Invoice): boolean => {
  return invoice.autoUpload;
};

export const isCredit = ({ kind }: { kind: InvoiceKindKey }): boolean =>
  kind === InvoiceKind.Credit;

export const isInvoiceFinal = ({ status }: { status: InvoiceStatusKey }): boolean =>
  (
    [
      InvoiceStatus.Approved,
      InvoiceStatus.PartialApproval,
      InvoiceStatus.Rejected,
    ] as InvoiceStatusKey[]
  ).includes(status);

/**
 * The approved amount to show for an invoice, or null when there is none yet.
 *
 * The API computes `approvedAmount` for every status and returns 0.00 for the
 * ones with no decision (pending, pending data, reset), which would read as
 * "approved for $0.00". Only a decided invoice has an approved amount, so any
 * other status shows nothing.
 */
export const invoiceApprovedAmountToShow = ({
  status,
  approvedAmount,
}: {
  status: InvoiceStatusKey;
  approvedAmount?: number | null;
}): number | null => (isInvoiceFinal({ status }) ? (approvedAmount ?? null) : null);

// The generated InvoiceDisplay model carries only {id, number}; a ticket's linked
// invoice serializes just those, so picking property/serviceProvider (required)
// threw on every ticket with a linked invoice.
export const invoiceDisplaySchema = invoiceSchema.pick({
  id: true,
  number: true,
});

// The `variant=form` invoice list (generated InvoiceForm serializer) trims the
// invoice to id/number plus the property and service-provider displays the
// ticket form needs to filter its dropdowns. Distinct from invoiceDisplaySchema,
// which mirrors the leaner InvoiceDisplay used for a ticket's linked invoice.
export const invoiceFormOptionSchema = invoiceSchema.pick({
  id: true,
  number: true,
  property: true,
  serviceProvider: true,
});
export type InvoiceFormOption = z.infer<typeof invoiceFormOptionSchema>;

export const invoiceFormValidator = (
  val: Partial<z.infer<typeof invoiceFormBaseSchema>>,
  ctx: z.RefinementCtx,
) => {
  const proposedAmount = val.proposedAmount ? parseFloat(val.proposedAmount.toFixed(2)) : 0.0;
  const lineItemsTotal = parseFloat(
    (val.lineItems ?? []).reduce((acc, curr) => acc + curr.amount, 0).toFixed(2),
  );
  if (val.isAdjustment && !val.parentInvoiceId) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Select the original invoice',
      path: ['parentInvoiceId'],
    });
  }
  if (lineItemsTotal > proposedAmount) {
    return ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'The total of the line items cannot be greater than the proposed amount',
      path: ['lineItems'],
    });
  }
  if (val.kind === InvoiceKind.Credit && val.creditTarget === 'invoice' && !val.creditedInvoiceId) {
    return ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Choose the invoice this credit applies to',
      path: ['creditedInvoiceId'],
    });
  }
  return true;
};

/* We should validate that line items do not add up to be greater than the proposedAmount but we do not need to validate that they are equal
 * because the proposedAmount is optional and the reviewedAmount is the one that is used to determine if the invoice is approved or not
 */
const invoiceFormBaseSchema = invoiceSchema
  .extend({
    id: z.string(),
    // The read `Invoice` type allows a null date (unextracted invoices), but the
    // create/edit form requires the user to supply a valid date — re-tighten it
    // here so form nullability doesn't leak in from the base schema.
    date: validDateTime().refine(validateDateYear, { message: VALID_DATE_YEAR_MESSAGE }),
    propertyId: z.string().uuid({ message: 'This field is required' }),
    consultantId: z.string().uuid({ message: 'This field is required' }),
    serviceProviderId: z.string().uuid({ message: 'This field is required' }),
    carIds: z.array(z.string()),
    /** Set when creating an adjusted invoice — links this invoice to its original. */
    parentInvoiceId: z.string().uuid().optional(),
    /** The reviewer marked this invoice as an adjustment and must pick its original. */
    isAdjustment: z.boolean().optional(),
    /** How the adjustment was created: via dispute agent or manually from the overview. */
    adjustmentSource: z.enum(['manual', 'agent']).optional(),
    /** Bypass the duplicate-invoice soft-block when the user confirms. */
    confirmDuplicate: z.boolean().optional(),
    // Empty means the credit applies to the whole account.
    creditedInvoiceId: z.string().uuid().nullish(),
    // Form-only: which of the two a credit applies to. Never sent to the API.
    creditTarget: z.enum(['account', 'invoice']).optional(),
  })
  .omit({
    property: true,
    carsServiced: true,
    consultant: true,
    serviceProvider: true,
    attachments: true,
    consultantGroup: true,
    relatedInvoices: true,
    parentInvoice: true,
    adjustments: true,
    creditedInvoice: true,
    credits: true,
    countsTowardSpend: true,
    approvedAmount: true,
    insights: true,
    insightStatus: true,
    source: true,
    emailContext: true,
    uploadedBy: true,
    uploadedAt: true,
    // Read-only, server-computed (SerializerMethodField); never accepted on
    // write, and the create/edit form never renders or submits it.
    activeContractId: true,
  } as const);

// The embedded `tickets[]` element: `{id, number, property}`, no `invoice` key.
// `property` arrives null when the ticket's property was deleted (SET_NULL).
const invoiceTicketSchema = z.object({
  id: z.string(),
  number: z.string(),
  property: z.string().nullable(),
});

const invoiceWithTicketsSchema = invoiceSchema.extend({
  tickets: z.array(invoiceTicketSchema),
});

export type Invoice = z.infer<typeof invoiceSchema>;
export type InvoiceInsight = z.infer<typeof insightSchema>;
export type InvoiceInsights = z.infer<typeof insightsSchema>;
export type AiReviewStatus = z.infer<typeof aiReviewStatus>;
export type InvoiceWithTickets = z.infer<typeof invoiceWithTicketsSchema>;
export type InvoiceWithPortfolio = InvoiceWithTickets & {
  portfolio: Portfolio | null;
};
export type InvoiceForm = z.infer<typeof invoiceFormSchema>;
// What the invoice form's fields hold. `autoUpload` carries a `.default()`, so
// it is absent from the values until the resolver parses them.
export type InvoiceFormValues = z.input<typeof invoiceFormSchema>;
/** `isInvoice` guard checks if an object is an `Invoice`'s instance */
export function isInvoice(object: unknown): object is Invoice {
  return invoiceKindValues.some((val) => val === (object as Invoice).kind);
}

export const invoiceFormSchema = invoiceFormBaseSchema.superRefine(invoiceFormValidator);

export const partialApproveInvoiceFormSchema = invoiceSchema
  .pick({
    reviewedAmount: true,
    reason: true,
    proposedAmount: true,
  })
  .extend({
    reason: z.string().min(1, { message: 'This field is required' }),
    rejectionCategory: z
      .array(z.enum(invoiceRejectionCategoryValues))
      .min(1, { message: 'This field is required' }),
    reviewedAmount: z.number().min(0, { message: 'This field is required' }),
    addStampedReason: z.boolean().optional(),
    savings: z.number().optional().default(0),
  })
  .superRefine((val, ctx) => {
    const proposedAmount = val.proposedAmount ? parseFloat(val.proposedAmount.toFixed(2)) : 0.0;
    const reviewedAmount = val.reviewedAmount ? parseFloat(val.reviewedAmount.toFixed(2)) : 0.0;
    if (reviewedAmount > proposedAmount) {
      return ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'The reviewed amount cannot be greater than the invoice amount',
        path: ['reviewedAmount'],
      });
    }
    return true;
  });

export type PartialApproveInvoiceForm = z.infer<typeof partialApproveInvoiceFormSchema>;
// `savings` carries a `.default()` and `proposedAmount` accepts the empty
// string the money input starts at, so what the fields hold is not what the
// resolver parses out.
export type PartialApproveInvoiceFormValues = z.input<typeof partialApproveInvoiceFormSchema>;

export const rejectInvoiceFormSchema = invoiceSchema
  .pick({
    reason: true,
    reviewedAmount: true,
    proposedAmount: true,
  })
  .extend({
    reason: z.string().min(1, { message: 'This field is required' }),
    rejectionCategory: z
      .array(z.enum(invoiceRejectionCategoryValues))
      .min(1, { message: 'This field is required' }),
    addStampedReason: z.boolean().optional(),
    savings: z.number().optional().default(0),
  });

export type RejectInvoiceForm = z.infer<typeof rejectInvoiceFormSchema>;
export type RejectInvoiceFormValues = z.input<typeof rejectInvoiceFormSchema>;

export const resetInvoiceFormSchema = z.object({ reason: z.string() });
export type ResetInvoiceForm = z.infer<typeof resetInvoiceFormSchema>;

export const approveInvoiceFormSchema = invoiceSchema
  .pick({
    reason: true,
    proposedAmount: true,
  })
  .extend({
    addStampedReason: z.boolean().optional(),
    savings: z.number().optional().default(0),
  });
export type ApproveInvoiceForm = z.infer<typeof approveInvoiceFormSchema>;
export type ApproveInvoiceFormValues = z.input<typeof approveInvoiceFormSchema>;

export const InvoiceNoteSchema = z.object({
  id: z.string(),
  createdAt: z.string(),
  content: z.string(),
  // Wire emits null for a note whose author was since deleted (SET_NULL) — the
  // RELATION nulls, not a field inside it, which the generated base misses.
  author: api_userSchema.nullable(),
});
export type InvoiceNote = z.infer<typeof InvoiceNoteSchema>;

///////////////////////////////////////////////////////////////////////////////
// Invoice write-response schema (PATCH / partial update)
//
// ⚠️ KNOWN BROKEN — do not build on this schema. The PATCH does not return the
// generated `InvoicesPartialUpdateResponse` shape: it returns `InvoiceSerializer`,
// which carries NESTED consultant/property/serviceProvider and no `*Id` keys at
// all, so this parse throws for every invoice. Not patchable here — a
// `.nullable()` override permits a null VALUE but keeps the key REQUIRED. The
// fix is a backend response annotation + regen.
export const invoiceWriteResponseSchema = InvoicesPartialUpdateResponse.extend({
  proposedAmount: decimalNumberNullable.optional(),
  reviewedAmount: decimalNumberNullable.optional(),
  savings: decimalNumberNullable.optional(),
  lineItems: z.array(lineItemSchema).optional(),
});
export type InvoiceWriteResponse = z.infer<typeof invoiceWriteResponseSchema>;

// The main invoices list envelope. `results` reuses the corrected
// `invoiceWithTicketsSchema` rather than the generated item, whose separately-
// generated copies of consultant/serviceProvider/parentInvoice/emailContext/
// uploadedBy repeat the same non-null gap — every invoice missing one of those
// would otherwise throw.
export const invoiceListResponseSchema = InvoicesListResponse.omit({ results: true }).extend({
  results: z.array(invoiceWithTicketsSchema),
});

// The `variant=form` invoice list envelope (SupportingDataForm's invoice
// dropdown) — same generated-annotation gap on `serviceProvider`, fixed the
// same way via the corrected `invoiceFormOptionSchema`.
export const invoiceFormListResponseSchema = InvoicesListResponse.omit({ results: true }).extend({
  results: z.array(invoiceFormOptionSchema),
});

export type InvoiceListMetadata = {
  numInvoices: number;
  numPendingInvoices: number;
  reviewedInvoices: number;
  invoiceAmount: number;
  afterReview: number;
  savings: number;
  rejectedDollars: number;
  projectedSavings: number;
  vendorCredits: number;
};
export type API_InvoiceFilterResponse = {
  results: InvoiceWithTickets[];
  count: number;
  metadata: InvoiceListMetadata;
};

export const API_InvoiceFilterSearchParams = z.object({
  limit: z.string().pipe(z.coerce.number()).optional(),
  offset: z.string().pipe(z.coerce.number()).optional(),
  ordering: z.string().optional().default('-date'),
  query: z.string().optional(),
  search: z.string().optional(),
  portfolio: z.string().array().optional(),
  tickets: z.string().optional(),
  serviceProvider: z.string().array().optional(),
  region: z.string().array().optional(),
  startDate: z.string().optional(),
  endDate: z.string().optional(),
  startDateStamped: z.string().optional(),
  rejectionCategory: z.string().array().optional(),
  endDateStamped: z.string().optional(),
  startDateUploaded: z.string().optional(),
  endDateUploaded: z.string().optional(),
  number: z.string().optional(),
  type: z.string().array().optional(),
  kind: z.string().array().optional(),
  propertyName: z.string().optional(),
  property: z.string().optional(),
  // Serviced-elevator ids, backing "other invoices & proposals for these elevators" on
  // the invoice detail page. Server-side because the alternative — paging a property's
  // whole invoice list and matching `carsServiced` in the browser — misses anything past
  // the first page.
  cars: z.string().array().optional(),
  status: z.string().array().optional(),
  disputeStatus: z.string().array().optional(),
  disputeNeedsAttention: z.boolean().optional(),
  // Loose string, not an enum: arbitrary values arrive from URL query params and
  // must be dropped, not thrown on. `fetchInvoicesFilter` is the enforcement
  // point — it dispatches to the matching envelope schema on this value.
  variant: z.string().optional(),
  proposedAmount: numberFilterParamsSchema.optional(),
  reviewedAmount: numberFilterParamsSchema.optional(),
  savings: numberFilterParamsSchema.optional(),
  relatedInvoices: z.string().optional(),
});
export type API_InvoiceFilterSearchParams = z.infer<typeof API_InvoiceFilterSearchParams>;
export type API_InvoiceFilterSearchParamsInput = z.input<typeof API_InvoiceFilterSearchParams>;

export type API_InvoiceCreateRequest = InvoiceForm;
export type API_InvoiceUpdateRequest = InvoiceForm & {
  relatedInvoices?: string[];
};
